import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import AgencyXLogo from 'images/shared/AgencyXLogo';

gsap.registerPlugin(ScrollTrigger);

const OurProcess = () => {
  // Track which segment is currently "on top" / highlighted.
  // Default to "insights" so it is active when the user first reaches the wheel.
  const [activeSegmentId, setActiveSegmentId] = useState('insights');

  // Ref for ScrollTrigger to control the wheel based on vertical scroll.
  // We attach this directly to the wheel container so the animation
  // only starts once the actual wheel is in view (not just the title).
  const wheelRef = useRef(null);

  const segments = [
    {
      id: 'insights',
      title: 'Insights',
      description: "Understand client's key challenges. Refine creative brief. Establish KPIs.",
      angle: 0
    },
    {
      id: 'strategy',
      title: 'Strategy',
      description:
        'Create an insight driven marketing/campaign strategy for the client based on the KPIs and resources available to hit the project timeline.',
      angle: 45
    },
    {
      id: 'creative',
      title: 'Creative Concept & Design',
      description: 'Establish brand concepts for the campaign.',
      angle: 90
    },
    {
      id: 'production',
      title: 'Production',
      description: 'Produce and prepare the campaign for launch.',
      angle: 135
    },
    {
      id: 'activation',
      title: 'Activation',
      description: 'Launch the campaign.',
      angle: 180
    },
    {
      id: 'distribution',
      title: 'Distribution',
      description: 'Push the campaign out.',
      angle: 225
    },
    {
      id: 'measurement',
      title: 'Measurement',
      description: 'Measure and record the performance of the campaign. Data generated.',
      angle: 270
    },
    {
      id: 'analysis',
      title: 'Analysis',
      description:
        'Analyse the data. Report insights on performance of the campaign to the end of the campaign.',
      angle: 315
    }
  ];

  // Compute rotation so that the currently active segment appears "on top".
  // Each segment is spaced 45 degrees apart, so we rotate the whole wheel
  // in 45-degree steps based on the active segment index.
  const activeIndex = segments.findIndex((segment) => segment.id === activeSegmentId);
  // Base rotation shifts the whole wheel back by two segments (-90deg)
  // so that "Insights" starts on the left instead of the right.
  const baseRotation = -90;
  const wheelRotation = activeIndex >= 0 ? baseRotation - activeIndex * 45 : baseRotation;

  // Use ScrollTrigger so vertical scrolling through the section
  // steps through the process and rotates the wheel.
  useEffect(() => {
    const wheel = wheelRef.current;
    if (!wheel) return;

    const totalSteps = segments.length - 1;

    const trigger = ScrollTrigger.create({
      trigger: wheel,
      // Start only once the entire wheel is fully inside the viewport:
      // when the bottom of the wheel hits the bottom of the viewport.
      // Then finish when the bottom reaches ~60%.
      start: 'bottom bottom',
      end: 'bottom 60%',
      scrub: true,
      onUpdate: (self) => {
        const index = Math.round(self.progress * totalSteps);
        const clampedIndex = Math.max(0, Math.min(totalSteps, index));
        const segment = segments[clampedIndex];
        if (segment) {
          setActiveSegmentId(segment.id);
        }
      }
    });

    return () => {
      trigger.kill();
    };
  }, []);

  const createSegmentPath = (index) => {
    const centerX = 300;
    const centerY = 300;
    const outerRadius = 190;
    const innerRadius = 70;
    const angleStep = 45;
    const gapAngle = 0.2; // Smaller gap to reduce outer edge gaps
    const startAngle = ((index * angleStep - 45 + gapAngle) * Math.PI) / 180; // Start one position back (-45 degrees)
    const endAngle = (((index + 1) * angleStep - 45 - gapAngle) * Math.PI) / 180;

    // Create trapezoid segments and points for the path
    const x1 = centerX + Math.cos(startAngle) * innerRadius;
    const y1 = centerY + Math.sin(startAngle) * innerRadius;
    const x2 = centerX + Math.cos(endAngle) * innerRadius;
    const y2 = centerY + Math.sin(endAngle) * innerRadius;
    const x3 = centerX + Math.cos(endAngle) * outerRadius;
    const y3 = centerY + Math.sin(endAngle) * outerRadius;
    const x4 = centerX + Math.cos(startAngle) * outerRadius;
    const y4 = centerY + Math.sin(startAngle) * outerRadius;

    return `M ${x1} ${y1} L ${x2} ${y2} L ${x3} ${y3} L ${x4} ${y4} Z`;
  };

  const createDescriptionSegmentPath = (index) => {
    const centerX = 300;
    const centerY = 300;
    const outerRadius = 280; // Larger outer radius for description segments
    const innerRadius = 195; // Start where main segments end
    const angleStep = 45;
    const gapAngle = 0.2;
    const startAngle = ((index * angleStep - 45 + gapAngle) * Math.PI) / 180;
    const endAngle = (((index + 1) * angleStep - 45 - gapAngle) * Math.PI) / 180;

    const x1 = centerX + Math.cos(startAngle) * innerRadius;
    const y1 = centerY + Math.sin(startAngle) * innerRadius;
    const x2 = centerX + Math.cos(endAngle) * innerRadius;
    const y2 = centerY + Math.sin(endAngle) * innerRadius;
    const x3 = centerX + Math.cos(endAngle) * outerRadius;
    const y3 = centerY + Math.sin(endAngle) * outerRadius;
    const x4 = centerX + Math.cos(startAngle) * outerRadius;
    const y4 = centerY + Math.sin(startAngle) * outerRadius;

    return `M ${x1} ${y1} L ${x2} ${y2} L ${x3} ${y3} L ${x4} ${y4} Z`;
  };

  const getTextPosition = (index) => {
    const centerX = 300;
    const centerY = 300;
    const outerRadius = 190;
    const innerRadius = 70;
    const angleStep = 45;
    const gapAngle = 0.5;

    // Calculate the segment's center angle
    const segmentStartAngle = ((index * angleStep - 45 + gapAngle) * Math.PI) / 180;
    const segmentEndAngle = (((index + 1) * angleStep - 45 - gapAngle) * Math.PI) / 180;
    const segmentCenterAngle = (segmentStartAngle + segmentEndAngle) / 2;

    // Position text at 75% towards the outer edge of the segment
    const textRadius = innerRadius + (outerRadius - innerRadius) * 0.75;

    const x = centerX + Math.cos(segmentCenterAngle) * textRadius;
    const y = centerY + Math.sin(segmentCenterAngle) * textRadius;

    return { x, y };
  };

  const getDescriptionTextPosition = (index) => {
    const centerX = 300;
    const centerY = 300;
    const outerRadius = 280;
    const innerRadius = 195;
    const angleStep = 45;
    const gapAngle = 0.2;

    // Calculate the segment's center angle
    const segmentStartAngle = ((index * angleStep - 45 + gapAngle) * Math.PI) / 180;
    const segmentEndAngle = (((index + 1) * angleStep - 45 - gapAngle) * Math.PI) / 180;
    const segmentCenterAngle = (segmentStartAngle + segmentEndAngle) / 2;

    // Position text in the middle of the description segment
    const textRadius = innerRadius + (outerRadius - innerRadius) * 0.5;

    const x = centerX + Math.cos(segmentCenterAngle) * textRadius;
    const y = centerY + Math.sin(segmentCenterAngle) * textRadius;

    return { x, y };
  };

  return (
    <Container>
      <div>
        <SectionTitle>Our Process</SectionTitle>
        <Anchors>
          {segments.map((segment) => (
            <Anchor type="button" onClick={() => setActiveSegmentId(segment.id)} key={segment.id} active={activeSegmentId === segment.id}>
              {segment.title}
            </Anchor>
          ))}
        </Anchors>
      </div>
      <WheelContainer ref={wheelRef}>
        <SVGWheel viewBox="0 0 600 600">
          {/* Center circle & logo stay static */}
          <circle cx="300" cy="300" r="65" fill="#f5f5f0" />

          <foreignObject x="250" y="250" width="100" height="100">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%'
              }}>
              <StyledAgencyXLogo />
            </div>
          </foreignObject>

          {/* Rotate only the outer/inner segments so the active one is visually "on top" */}
          <g
            style={{
              transform: `rotate(${wheelRotation}deg)`,
              transformOrigin: '50% 50%',
              transition: 'transform 0.5s ease'
            }}>
            {/* Description segments (outer ring) */}
            {segments.map((segment, index) => {
              const descTextPos = getDescriptionTextPosition(index);
              return (
                <g key={`${segment.id}-desc`}>
                  <path d={createDescriptionSegmentPath(index)} fill="#f5f5f0" />
                  <text
                    x={descTextPos.x}
                    y={descTextPos.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="#666"
                    fontSize="10"
                    fontWeight="400"
                    transform={`rotate(${index * 45 + 67} ${descTextPos.x} ${descTextPos.y})`}
                    style={{
                      pointerEvents: 'none'
                    }}>
                    <tspan x={descTextPos.x} dy="-0.5em">
                      {segment.description.split(' ').slice(0, 4).join(' ')}
                    </tspan>
                    <tspan x={descTextPos.x} dy="1.2em">
                      {segment.description.split(' ').slice(4, 8).join(' ')}
                    </tspan>
                    <tspan x={descTextPos.x} dy="1.2em">
                      {segment.description.split(' ').slice(8, 12).join(' ')}
                    </tspan>
                  </text>
                </g>
              );
            })}

            {/* Main segments */}
            {segments.map((segment, index) => {
              const textPos = getTextPosition(index);
              const isActive = segment.id === activeSegmentId;

              return (
                <g
                  key={segment.id}
                  // Use click instead of hover so the wheel
                  // doesn't continuously spin as the cursor
                  // passes over adjacent segments.
                  onClick={() => setActiveSegmentId(segment.id)}
                  style={{ cursor: 'pointer' }}>
                  <path
                    d={createSegmentPath(index)}
                    fill="var(--ax-gold, #DAA520)"
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      // Darken the active segment to 0.8 brightness
                      filter: isActive ? 'brightness(0.8)' : 'brightness(1)'
                    }}
                  />
                  <text
                    x={textPos.x}
                    y={textPos.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize="12"
                    fontWeight="700"
                    transform={`rotate(${index * 45 + 67} ${textPos.x} ${textPos.y})`}
                    style={{
                      pointerEvents: 'none'
                    }}>
                    {segment.title.length > 15 ? (
                      <>
                        <tspan x={textPos.x} dy="-0.4em">
                          {segment.title.split(' ').slice(0, 2).join(' ')}
                        </tspan>
                        <tspan x={textPos.x} dy="1.1em">
                          {segment.title.split(' ').slice(2).join(' ')}
                        </tspan>
                      </>
                    ) : (
                      segment.title
                    )}
                  </text>
                </g>
              );
            })}
          </g>
        </SVGWheel>
      </WheelContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  @media screen and (min-width: 48rem) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
  }
`;

const SectionTitle = styled.h3`
  font-weight: 900;
  font-size: 3rem;

  @media (min-width: 768px) {
    font-size: 4rem;
  }
`;

const WheelContainer = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    width: 600px;
    height: 600px;
  }

  @media (min-width: 1024px) {
    width: 700px;
    height: 700px;
  }
`;

const SVGWheel = styled.svg`
  width: 100%;
  height: 100%;
`;

const StyledAgencyXLogo = styled(AgencyXLogo)`
  width: 180px;

  path {
    fill: var(--ax-black);

    &:nth-child(2) {
      fill: var(--ax-gold);
    }
  }
`;

const Anchors = styled.div`
  display: none;
  margin-top: 2rem;
  @media (min-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const Anchor = styled.button`
  align-items: center;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-weight: 600;
  gap: 1rem;
  outline: none;
  color: var(--ax-gold);
  opacity: ${({ active }) => active ? '1' : '0.4'};
  text-align: left;
  transition: opacity 0.25s ease;
  text-transform: uppercase;
  font-size: 2rem;
  &:before {
    background: currentColor;
    border-radius: 50%;
    content: '';
    display: block;
    flex-shrink: 0;
    height: min(0.4em, 10px);
    transition: background 0.25s ease;
    width: min(0.4em, 10px);
  }

  &:hover {
    opacity: 1;
  }
`;

export default OurProcess;
